import {
  Button,
  Checkbox,
  Select,
  SelectOption,
  Spacer,
  Stack,
  TextBody,
  TextCaption,
  TextTitle,
} from '@pypestream/design-system';
import { createDefaultWidgetAppConfig } from '@pypestream/pype/customization';
import { FC, useCallback, useEffect, useMemo, useState } from 'react';

import {
  GenericModal,
  GenericModalProps,
  useGenericModal,
} from '../components';
import {
  buildEngagementApiUrl,
  toAutorespondersInput,
  toPlatformMessages,
  type Channel,
} from '../utils';
import {
  sendProStudioEvent,
  useProStudioCtxSelector,
} from '../xstate/app.xstate';
import { sendUserMessage } from '../xstate/app.xstate-utils';
import {
  awaitsDesignPlan,
  copyLegacyStyling,
  describeDesignCopy,
  describeDesignCopyResult,
  diffLegacyStyling,
  diffMessages,
  messagesAvailability,
  resolveDesignCopy,
  stylingAvailability,
  type SettingChange,
} from './copy-channel-settings';
import { loadPypeNextDesign, type DesignLoad } from './load-pype-next-design';
import { usePairDesigns } from './use-pair-designs';
import { mutateWidgetAppDesign } from './pype-designer/mutate-widget-app-design';
import { withResolvedDefaults } from './pype-designer/pype-designer-utils';
import { resolvePypeNextAccess } from './pype-designer/resolve-pype-next-access';

import './copy-channel-settings-modal.scss';
import { singleSelectValue } from '../utils/single-select-value';

const envLabel = (env: Channel['env']) => (env === 'live' ? 'Live' : 'Sandbox');
const channelLabel = (c: Channel) => `${c.name} (${envLabel(c.env)})`;

const loadDesign = (channel: Channel): Promise<DesignLoad> =>
  loadPypeNextDesign({
    appId: channel.id,
    engagementApiUrl: buildEngagementApiUrl(
      window.location.origin,
      channel.env
    ),
  });

/** The target's design when it has none saved: the same seed the Pype Designer uses. */
const seedDesign = () => withResolvedDefaults(createDefaultWidgetAppConfig());

/** Resolve on the event's `cb`, reject on its `errorCb`. */
const awaitEvent = (
  send: (callbacks: { cb: () => void; errorCb: () => void }) => void
) =>
  new Promise<void>((resolve, reject) =>
    send({ cb: resolve, errorCb: () => reject(new Error('save failed')) })
  );

const ChangeList: FC<{ title: string; changes: SettingChange[] }> = ({
  title,
  changes,
}) => (
  <div className="c-copy-settings__changes">
    <TextBody size="small">{title}</TextBody>
    {changes.length === 0 ? (
      <TextCaption variant="tertiary">
        Already the same — nothing changes.
      </TextCaption>
    ) : (
      <ul>
        {changes.map((c) => (
          <li key={c.label}>
            <span className="c-copy-settings__label">{c.label}</span>
            <span className="c-copy-settings__from">{c.from}</span>
            <span aria-hidden="true">→</span>
            <span className="c-copy-settings__to">{c.to}</span>
          </li>
        ))}
      </ul>
    )}
  </div>
);

type CopySettingsModalProps = GenericModalProps & {
  source: Channel | null;
  channels: Channel[];
};

/**
 * "Copy settings…" — copy a channel's default messages and/or styling onto
 * another channel of the same solution. See `copy-channel-settings.ts` for
 * what moves and what never does.
 */
const CopySettingsModal: FC<CopySettingsModalProps> = ({
  source,
  channels,
  modalRef,
  onClose: onCloseProp,
}) => {
  const { defaultMessagesPypes, featureFlags, projectId } =
    useProStudioCtxSelector((ctx) => ({
      defaultMessagesPypes: ctx.defaultMessagesPypes,
      featureFlags: ctx.featureFlags,
      projectId: ctx.project?.id,
    }));
  const pypeNext = resolvePypeNextAccess(featureFlags) === 'allowed';

  const targets = useMemo(
    () => channels.filter((c) => source && c.id !== source.id),
    [channels, source]
  );
  const [targetId, setTargetId] = useState<string>('');
  const [wantMessages, setWantMessages] = useState(true);
  const [wantStyling, setWantStyling] = useState(true);
  const [step, setStep] = useState<'choose' | 'confirm'>('choose');
  const [saving, setSaving] = useState(false);

  const target = targets.find((c) => c.id === targetId) ?? null;
  // The designs are only ever read for the pair they were loaded for; a load
  // that is abandoned (Back, close, another source) drops its result.
  const {
    designs: pairDesigns,
    start: startDesignLoad,
    cancel: cancelDesignLoad,
  } = usePairDesigns(source, target, loadDesign);

  useEffect(() => {
    if (defaultMessagesPypes == null) {
      sendProStudioEvent({ type: 'loadDefaultMessages' });
    }
  }, [defaultMessagesPypes]);

  const pypeOf = (channel: Channel) =>
    defaultMessagesPypes?.[channel.env]?.find((p) => p.id === channel.pypeId);

  const messagesState =
    source && target
      ? messagesAvailability(source, target, (pypeId) =>
          [
            ...(defaultMessagesPypes?.live ?? []),
            ...(defaultMessagesPypes?.sandbox ?? []),
          ].some((p) => p.id === pypeId)
        )
      : null;
  const stylingState =
    source && target ? stylingAvailability(source, target) : null;

  const doMessages = !!messagesState?.available && wantMessages;
  const doStyling = !!stylingState?.available && wantStyling;

  const designPlan = useMemo(
    () =>
      pairDesigns
        ? resolveDesignCopy(pairDesigns.source, pairDesigns.target, seedDesign)
        : null,
    [pairDesigns]
  );

  const reset = useCallback(() => {
    cancelDesignLoad();
    setTargetId('');
    setWantMessages(true);
    setWantStyling(true);
    setStep('choose');
    setSaving(false);
  }, [cancelDesignLoad]);
  // The modal stays mounted between uses, so a different source starts from
  // scratch even if the host did not close it first: a load still in flight for
  // the old source must not leave this one waiting on a pair that is gone.
  const sourceId = source?.id;
  useEffect(() => {
    reset();
  }, [sourceId, reset]);
  const onClose = () => {
    reset();
    onCloseProp?.();
  };

  const goBack = () => {
    cancelDesignLoad();
    setStep('choose');
  };

  const review = async () => {
    setStep('confirm');
    if (doStyling && pypeNext && source && target) {
      await startDesignLoad();
    } else {
      cancelDesignLoad();
    }
  };

  const messageChanges =
    doMessages && source && target && pypeOf(source) && pypeOf(target)
      ? diffMessages(
          toPlatformMessages(pypeOf(source)!),
          toPlatformMessages(pypeOf(target)!)
        )
      : [];
  const stylingChanges =
    doStyling && source && target ? diffLegacyStyling(source, target) : [];

  const apply = async () => {
    if (!source || !target || !projectId) return;
    if (awaitsDesignPlan(doStyling, pypeNext, designPlan)) return;
    setSaving(true);
    const done: string[] = [];
    const failed: string[] = [];
    // Said in the result when the Pype Next part was skipped for a failed load;
    // a skipped-because-absent source needs no note, the confirm step said it.
    let designNote = '';

    if (doMessages) {
      const sourcePype = pypeOf(source);
      try {
        if (!sourcePype) throw new Error('no source messages');
        await awaitEvent((callbacks) =>
          sendProStudioEvent({
            type: 'updateDefaultMessages',
            payload: {
              id: target.pypeId,
              env: target.env,
              autoresponders: toAutorespondersInput(
                toPlatformMessages(sourcePype)
              ),
              ...callbacks,
            },
          })
        );
        done.push('default messages');
      } catch {
        failed.push('default messages');
      }
    }

    if (doStyling) {
      try {
        await awaitEvent((callbacks) =>
          sendProStudioEvent({
            type: 'updateChannel',
            payload: {
              ...target,
              ...copyLegacyStyling(source, target),
              id: target.id,
              ...callbacks,
            },
          })
        );
        done.push('styling & CSS');
      } catch {
        failed.push('styling & CSS');
      }

      if (pypeNext && designPlan?.copy) {
        try {
          await mutateWidgetAppDesign({
            appId: target.id,
            env: target.env,
            projectId,
            config: designPlan.config,
          });
          done.push('Pype Next design');
        } catch {
          failed.push('Pype Next design');
        }
      } else if (
        pypeNext &&
        designPlan &&
        !designPlan.copy &&
        designPlan.reason !== 'source-absent'
      ) {
        designNote = describeDesignCopyResult(
          designPlan,
          source.name,
          target.name
        );
      }
    }

    setSaving(false);
    if (failed.length === 0) {
      sendUserMessage({
        type: designNote ? 'warning' : 'success',
        text:
          `Copied ${done.join(', ')} to ${target.name}.` +
          (designNote ? ` ${designNote}` : ''),
        autoClose: designNote ? 8000 : 3000,
      });
      onClose();
    } else {
      sendUserMessage({
        type: 'error',
        text:
          (done.length ? `Copied ${done.join(', ')}, but ` : '') +
          `couldn't copy ${failed.join(', ')} to ${target.name}.`,
        autoClose: 6000,
      });
    }
  };

  if (!source) return null;

  return (
    <GenericModal
      test-id="bobcat-copy-settings-modal"
      modalRef={modalRef}
      onClose={onClose}
      onError={() => setSaving(false)}
      hideCloseBtn={saving}
      stayOnClickOutside={saving}
    >
      <Stack slot="header" direction="column" gutter="2xsmall">
        <TextTitle size="small">Copy settings</TextTitle>
        <TextBody variant="secondary">From {channelLabel(source)}</TextBody>
      </Stack>

      <div className="c-copy-settings">
        {step === 'choose' ? (
          <>
            <TextBody size="small">Copy to</TextBody>
            <Spacer size="2xsmall" />
            <Select
              test-id="bobcat-copy-settings-target"
              variant="outlined"
              size="small"
              value={targetId}
              placeholder="Choose a channel"
              onChange={(e) => setTargetId(singleSelectValue(e.target.value))}
            >
              {targets.map((c) => (
                <SelectOption key={c.id} value={c.id}>
                  {channelLabel(c)}
                </SelectOption>
              ))}
            </Select>
            {targets.length === 0 && (
              <TextCaption variant="tertiary">
                This solution has no other channel to copy to.
              </TextCaption>
            )}

            <Spacer size="medium" />
            <TextBody size="small">What to copy</TextBody>
            <Spacer size="2xsmall" />
            <Checkbox
              test-id="bobcat-copy-settings-messages"
              size="small"
              checked={target ? doMessages : wantMessages}
              disabled={!!messagesState && !messagesState.available}
              onChange={(e) => setWantMessages(e.target.checked)}
            >
              Default messages (offline, busy, technical error)
            </Checkbox>
            {messagesState && !messagesState.available && (
              <TextCaption variant="tertiary">
                {messagesState.reason}
              </TextCaption>
            )}
            <Spacer size="xsmall" />
            <Checkbox
              test-id="bobcat-copy-settings-styling"
              size="small"
              checked={target ? doStyling : wantStyling}
              disabled={!!stylingState && !stylingState.available}
              onChange={(e) => setWantStyling(e.target.checked)}
            >
              Styling &amp; CSS
            </Checkbox>
            {stylingState && !stylingState.available && (
              <TextCaption variant="tertiary">
                {stylingState.reason}
              </TextCaption>
            )}
            <Spacer size="small" />
            <TextCaption variant="secondary">
              {pypeNext
                ? 'Names, launcher text and connection settings are never copied.'
                : 'Names, logos, launcher text and connection settings are never copied.'}
            </TextCaption>
          </>
        ) : (
          target && (
            <>
              <TextBody size="small">
                Replace {channelLabel(target)}&apos;s{' '}
                {[doMessages && 'default messages', doStyling && 'styling']
                  .filter(Boolean)
                  .join(' and ')}{' '}
                with {source.name}&apos;s?
              </TextBody>
              <Spacer size="small" />
              {doMessages && (
                <ChangeList title="Default messages" changes={messageChanges} />
              )}
              {doStyling && (
                <ChangeList title="Styling & CSS" changes={stylingChanges} />
              )}
              {doStyling && pypeNext && (
                <TextCaption variant="secondary" role="status">
                  {designPlan
                    ? describeDesignCopy(designPlan, source.name, target.name)
                    : 'Checking the Pype Next design…'}
                </TextCaption>
              )}
            </>
          )
        )}
      </div>

      <Stack slot="footer" justifyContent="end">
        {step === 'choose' ? (
          <>
            <Button variant="ghost" size="large" onClick={onClose}>
              Cancel
            </Button>
            <Button
              test-id="bobcat-copy-settings-review"
              size="large"
              disabled={!target || (!doMessages && !doStyling)}
              onClick={review}
            >
              Review
            </Button>
          </>
        ) : (
          <>
            <Button
              variant="ghost"
              size="large"
              disabled={saving}
              onClick={goBack}
            >
              Back
            </Button>
            <Button
              test-id="bobcat-copy-settings-apply"
              size="large"
              disabled={
                saving || awaitsDesignPlan(doStyling, pypeNext, designPlan)
              }
              onClick={apply}
            >
              {saving ? 'Copying…' : 'Copy settings'}
            </Button>
          </>
        )}
      </Stack>
    </GenericModal>
  );
};

export const useCopySettingsModal = (props: {
  source: Channel | null;
  channels: Channel[];
}) => {
  const { modalRef, openModal, closeModal } = useGenericModal();
  return {
    openModal,
    copySettingsModal: (
      <CopySettingsModal
        modalRef={modalRef}
        onClose={closeModal}
        source={props.source}
        channels={props.channels}
      />
    ),
  };
};
